import { useParams } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Gavel, Trophy } from 'lucide-react'
import type { ApiError } from '@/lib/api'
import { api } from '@/lib/api'
import { localizedName } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useT, getT } from '@/lib/i18n'
import {
  StorefrontCard,
  StorefrontEmptyState,
  StorefrontLoading,
  StorefrontPage,
} from '@/components/StorefrontPage'
import type { AuctionDetail } from '@/shared/types'

interface PublicSettings {
  auctions_enabled: boolean
}

function formatTimeLeft(seconds: number, lang: 'uk' | 'en' | 'ru') {
  if (seconds <= 0) return getT(lang, 'auction').ended
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (lang === 'en') return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`
  if (lang === 'ru') return h > 0 ? `${h} ч ${m} мин` : m > 0 ? `${m} мин ${s} с` : `${s} с`
  return h > 0 ? `${h} год ${m} хв` : m > 0 ? `${m} хв ${s} с` : `${s} с`
}

export function AuctionPage() {
  const { auctionId } = useParams({ from: '/auctions/$auctionId' })
  const lang = useAppStore((s) => s.lang)
  const T = useT('auction')

  const { data: settings, isLoading: settingsLoading } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })
  const auctionsEnabled = Boolean(settings?.auctions_enabled)
  const { data: auction, isLoading, isError, refetch } = useQuery<AuctionDetail>({
    queryKey: ['auction', auctionId],
    queryFn: () => api.get<AuctionDetail>(`/auctions/${auctionId}`),
    refetchInterval: 10_000,
    enabled: auctionsEnabled,
  })

  const bidMutation = useMutation<{ success: boolean; new_price?: number }, ApiError, number>({
    mutationFn: (amount) => api.post(`/auctions/${auctionId}/bid`, { amount }),
    onSuccess: async () => {
      haptic('success')
      await refetch()
    },
    onError: () => haptic('error'),
  })

  if (settingsLoading || (auctionsEnabled && isLoading)) {
    return (
      <StorefrontPage backTo="/auctions" backLabel={T.back}>
        <StorefrontLoading />
      </StorefrontPage>
    )
  }

  if (!auctionsEnabled || isError || !auction) {
    return (
      <StorefrontPage backTo="/auctions" backLabel={T.back}>
        <StorefrontEmptyState
          icon={Gavel}
          title={auctionsEnabled ? T.notFound : T.disabled}
        />
      </StorefrontPage>
    )
  }

  const minBid = auction.current_price + auction.min_step
  const options = [minBid, minBid + auction.min_step, minBid + auction.min_step * 5]
  const canBid = auction.status === 'active' && auction.time_left_seconds > 0 && !auction.is_winning
  const productName = localizedName(lang, auction.product_name_uk, auction.product_name_en)

  return (
    <StorefrontPage backTo="/auctions" backLabel={T.back}>
      <StorefrontCard className="overflow-hidden">
        {auction.photo_url ? (
          <img src={auction.photo_url} alt={productName} className="h-72 w-full object-cover" />
        ) : (
          <div className="flex h-64 items-center justify-center bg-slate-100 text-6xl">🔨</div>
        )}
        <div className="p-4 sm:p-5">
          <h1 className="text-xl font-bold text-slate-900">{productName}</h1>
          <div className="mt-2.5 flex items-baseline gap-2">
            <span className="text-2xl font-black text-brand-600">{auction.current_price} UAH</span>
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            {auction.bids_count} {T.bids} • {formatTimeLeft(auction.time_left_seconds, lang)}
          </p>
          {auction.is_winning && (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 border border-emerald-200">
              <Trophy className="h-3.5 w-3.5" />
              {T.winning}
            </p>
          )}

          {canBid && (
            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              {options.map((amount, idx) => (
                <button
                  key={`${amount}-${idx}`}
                  type="button"
                  disabled={bidMutation.isPending}
                  onClick={() => { haptic('medium'); bidMutation.mutate(amount) }}
                  className={`rounded-xl px-4 py-3 text-xs font-bold transition shadow-xs active:scale-95 ${
                    idx === 2
                      ? 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                      : 'bg-brand-600 text-white hover:bg-brand-700'
                  }`}
                >
                  {amount} UAH
                </button>
              ))}
            </div>
          )}

          {auction.bids.length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">{T.recentBids}</p>
              <div className="space-y-1.5">
                {auction.bids.map((bid, idx) => (
                  <div
                    key={`${bid.amount}-${bid.created_at}-${idx}`}
                    className="flex items-center justify-between rounded-xl bg-slate-50/80 px-3 py-2 text-xs text-slate-700"
                  >
                    <span className="font-medium">{bid.is_mine ? T.you : T.bidder}</span>
                    <span className="font-bold text-slate-900">{bid.amount} UAH</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </StorefrontCard>
    </StorefrontPage>
  )
}
